/**
 *  @class
 *  @function ThemeFooter
 */

if (!customElements.get('theme-footer')) {
  class ThemeFooter extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.animations_enabled = this.dataset.footerRevealAnimation === 'true' &&
        document.body.classList.contains('animations-true');

      if (document.body.classList.contains('template-product-quick-view')) {
        this.animations_enabled = false;
      }
      if (!this.animations_enabled) {
        window.__bassdashFooterMarketSwitcher?.init(this);
        return;
      }
      if (typeof gsap === 'undefined') {
        window.addEventListener('load', () => this.connectedCallback(), { once: true });
        window.__bassdashFooterMarketSwitcher?.init(this);
        return;
      }
      this.content = document.getElementById('main-content');
      this.wrapper = document.getElementById('wrapper');
      if (!this.content || !this.wrapper) {
        window.__bassdashFooterMarketSwitcher?.init(this);
        return;
      }
      this.footer_bg = window.getComputedStyle(document.documentElement).getPropertyValue('--color-footer-bg');
      this.radius = window.getComputedStyle(document.documentElement).getPropertyValue('--block-border-radius');
      this.setupAnimations();
      window.__bassdashFooterMarketSwitcher?.init(this);
    }
    setupAnimations() {
      let mm = gsap.matchMedia();
      this.wrapper.style.backgroundColor = this.footer_bg;
      mm.add({
        // set up any number of arbitrarily-named conditions. The function below will be called when ANY of them match.
        isDesktop: `(min-width: 768px)`,
        reduceMotion: "(prefers-reduced-motion: reduce)"
      }, (context) => {

        // context.conditions has a boolean property for each condition defined above indicating if it's matched or not.
        let {
          isDesktop,
          isMobile,
          reduceMotion
        } = context.conditions;

        gsap.to(this.content, {
          "--footer-border-radius": !isDesktop || reduceMotion ? '0px' : `${this.radius}`,
          "--footer-width-percent": !isDesktop || reduceMotion ? '0%' : '4%',
          duration: 0.5,
          inherit: false,
          ease: "none",
          modifiers: {
            "--footer-border-radius": x => Math.round(parseFloat(x)) + "px",
            "--footer-width-percent": x => Math.round(parseFloat(x) * 100) / 100 + "%"
          },
          scrollTrigger: {
            trigger: this,
            fastScrollEnd: true,
            scrub: 1,
            start: () => `top bottom`,
            end: () => `bottom bottom`
          }
        });
      });

    }
  }
  customElements.define('theme-footer', ThemeFooter);
}

(() => {
  const CHECKED_KEY = 'bassdash_market_redirect_checked';
  const PREFERENCE_KEY = 'bassdash_market_preference';
  const STORAGE_VALUE = 'v1';
  const MANUAL_PARAM = 'bd_market_selected';
  const SWITCHER_SELECTOR = '[data-footer-market-switcher]';
  const OPTION_SELECTOR = '[data-market-option]';
  const OPEN_CLASS = 'footer-market-switcher--open';
  let redirectStarted = false;

  if (window.__bassdashFooterMarketSwitcher) {
    window.__bassdashFooterMarketSwitcher.init();
    return;
  }

  const safeStorage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
      }
    }
  };

  function getCookie(name) {
    return document.cookie
      .split('; ')
      .map((item) => item.split('='))
      .find(([key]) => key === name)?.[1];
  }

  function getStored(key) {
    return safeStorage.get(key) || (getCookie(key) ? decodeURIComponent(getCookie(key)) : null);
  }

  function normalizeHost(hostname) {
    return hostname.toLowerCase().replace(/^www\./, '');
  }

  function isCurrentSite(url) {
    const target = new URL(url, window.location.href);
    return normalizeHost(target.hostname) === normalizeHost(window.location.hostname);
  }

  function isKnownMarketHost(targets) {
    const currentHost = normalizeHost(window.location.hostname);

    return Array.from(targets.values()).some((url) => {
      const target = new URL(url, window.location.href);
      return normalizeHost(target.hostname) === currentHost;
    });
  }

  function withParam(url, name, value) {
    const target = new URL(url, window.location.href);
    target.searchParams.set(name, value);
    return target.toString();
  }

  function cleanUrlParams() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(MANUAL_PARAM)) {
      return;
    }

    url.searchParams.delete(MANUAL_PARAM);
    window.history.replaceState({}, '', url.toString());
  }

  function closeSwitcher(switcher) {
    const button = switcher.querySelector('.footer-market-switcher__button');
    switcher.classList.remove(OPEN_CLASS);
    button?.setAttribute('aria-expanded', 'false');
  }

  function closeAllSwitchers(except) {
    document.querySelectorAll(SWITCHER_SELECTOR).forEach((switcher) => {
      if (switcher !== except) {
        closeSwitcher(switcher);
      }
    });
  }

  function openSwitcher(switcher) {
    const button = switcher.querySelector('.footer-market-switcher__button');
    closeAllSwitchers(switcher);
    switcher.classList.add(OPEN_CLASS);
    button?.setAttribute('aria-expanded', 'true');
  }

  function toggleSwitcher(switcher) {
    if (switcher.classList.contains(OPEN_CLASS)) {
      closeSwitcher(switcher);
    } else {
      openSwitcher(switcher);
    }
  }

  function getOptions(switcher) {
    return Array.from(switcher.querySelectorAll(OPTION_SELECTOR));
  }

  function focusRelativeOption(switcher, direction) {
    const options = getOptions(switcher);
    const activeIndex = options.indexOf(document.activeElement);
    const nextIndex = activeIndex === -1
      ? 0
      : (activeIndex + direction + options.length) % options.length;

    openSwitcher(switcher);
    options[nextIndex]?.focus();
  }

  function markManualSelection(code) {
    safeStorage.set(CHECKED_KEY, STORAGE_VALUE);
    safeStorage.set(PREFERENCE_KEY, code);
  }

  function setSwitcherSelection(code) {
    if (!code) {
      return;
    }

    document.querySelectorAll(SWITCHER_SELECTOR).forEach((switcher) => {
      const selectedOption = switcher.querySelector(`${OPTION_SELECTOR}[data-market-code="${code}"]`);
      const current = switcher.querySelector('[data-market-current]');

      if (!selectedOption) {
        return;
      }

      getOptions(switcher).forEach((option) => {
        const isSelected = option === selectedOption;
        option.classList.toggle('footer-market-switcher__option--active', isSelected);
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      if (current) {
        current.innerHTML = selectedOption.querySelector('.footer-market-switcher__option-content')?.innerHTML || current.innerHTML;
      }
    });
  }

  function setupSwitcher(switcher) {
    if (switcher.dataset.marketSwitcherReady === 'true') {
      return;
    }

    const button = switcher.querySelector('.footer-market-switcher__button');
    const options = getOptions(switcher);

    if (!button || !options.length) {
      return;
    }

    switcher.dataset.marketSwitcherReady = 'true';

    button.addEventListener('click', () => toggleSwitcher(switcher));
    button.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusRelativeOption(switcher, 1);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusRelativeOption(switcher, -1);
      }
    });

    options.forEach((option) => {
      option.addEventListener('click', (event) => {
        const code = option.dataset.marketCode;

        if (!code || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }

        markManualSelection(code);
        option.href = withParam(option.href, MANUAL_PARAM, code);
      });

      option.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusRelativeOption(switcher, 1);
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          focusRelativeOption(switcher, -1);
        }

        if (event.key === 'Escape') {
          closeSwitcher(switcher);
          button.focus();
        }
      });
    });
  }

  function getDetectedCountry() {
    const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
    const suggestionsUrl = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
    const currentCountry = window.Shopify?.country;

    suggestionsUrl.searchParams.set('country[enabled]', 'true');

    if (currentCountry) {
      suggestionsUrl.searchParams.set('country[exclude]', currentCountry);
    }

    return fetch(suggestionsUrl.toString())
      .then((response) => {
        if (!response.ok) {
          return '';
        }

        return response.json();
      })
      .then((data) => {
        return (data?.detected_values?.country?.handle || '').toUpperCase();
      })
      .catch(() => '');
  }

  function getMarketTargets() {
    const targets = new Map();

    document.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
      const code = option.dataset.marketCode;

      if (code && option.href && !targets.has(code)) {
        targets.set(code, option.href);
      }
    });

    return targets;
  }

  function handleManualLanding() {
    const params = new URLSearchParams(window.location.search);
    const selectedMarket = params.get(MANUAL_PARAM);

    if (!selectedMarket) {
      return false;
    }

    markManualSelection(selectedMarket.toUpperCase());
    setSwitcherSelection(selectedMarket.toUpperCase());
    cleanUrlParams();
    return true;
  }

  function redirectFirstVisit() {
    if (redirectStarted || handleManualLanding() || getStored(CHECKED_KEY) === STORAGE_VALUE || getStored(PREFERENCE_KEY)) {
      return;
    }

    const targets = getMarketTargets();

    if (!isKnownMarketHost(targets)) {
      safeStorage.set(CHECKED_KEY, STORAGE_VALUE);
      return;
    }

    redirectStarted = true;

    getDetectedCountry().then((detectedCountry) => {
      const targetUrl = targets.get(detectedCountry);

      safeStorage.set(CHECKED_KEY, STORAGE_VALUE);

      if (!targetUrl || isCurrentSite(targetUrl)) {
        setSwitcherSelection(detectedCountry);
        return;
      }

      window.location.replace(withParam(targetUrl, MANUAL_PARAM, detectedCountry));
    });
  }

  function init(root = document) {
    const context = root?.target instanceof Element ? root.target : root;
    const switchers = context.matches?.(SWITCHER_SELECTOR)
      ? [context]
      : Array.from(context.querySelectorAll?.(SWITCHER_SELECTOR) || []);

    if (!switchers.length) {
      return;
    }

    switchers.forEach(setupSwitcher);
    redirectFirstVisit();
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest(SWITCHER_SELECTOR)) {
      closeAllSwitchers();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllSwitchers();
    }
  });

  document.addEventListener('shopify:section:load', init);

  window.__bassdashFooterMarketSwitcher = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
})();
